import { IVisualizationSettings } from './interfaces'

export const DEVELOPMENT = process.env.NODE_ENV === 'development'
export const PRODUCTION = process.env.NODE_ENV === 'production'
export const REACT_APP_I18N_DEBUG = DEVELOPMENT && process.env.REACT_APP_I18N_DEBUG === 'true'

type RuntimeConfig = { DOMAIN?: string }

const getRuntimeDomain = (): string => {
  if (typeof window === 'undefined') return ''
  const appConfig = (window as any).__APP_CONFIG__ as RuntimeConfig | undefined
  const domainFromConfig = String(appConfig?.DOMAIN || '').trim()
  if (domainFromConfig) return domainFromConfig
  return String(window.location.hostname || '').trim()
}

export const DOMAIN = getRuntimeDomain()
export const GAUSIUM_ENABLED = !DOMAIN.toLowerCase().includes('robo')
export const ASBIS_ENABLED = false
export const SHOW_AI_AGENT = !DOMAIN.toLowerCase().includes('robo')

export enum PATH {
  SIGNIN = '/signin',
  RESTORE_PASSWORD = '/restore-password',
  NEW_PASSWORD = '/new-password',
  MISSION_LIST = '/missions',
  FACILITY_LIST = '/facilities',
  FACILITY = '/facilities/',
  FACILITY_ID = '/facilities/:id',
  SCENARIOS = '/scenarios/',
  SCENARIO_ID = '/scenarios/:id',
  SCENARIO_LIST = '/scenarios',
  MISSION = '/mission/',
  MISSION_ID = '/mission/:id',
  ASSET_LIST = '/',
  STATION_LIST = '/stations',
  RECORDED_ROUTE_LIST = '/recorded-routes',
  STATION = '/station/',
  STATION_ID = '/station/:id',
  MAP_LIST = '/maps',
  /** Main calendar: normalized statistics + planned tasks (Gausium-capable). */
  CALENDAR = '/calendar',
  /** Legacy scheduler (direct link only). */
  CALENDAR_LEGACY = '/calendar-legacy',
  /** @deprecated Use `PATH.CALENDAR` — redirects to the same normalized calendar. */
  CALENDAR_NORMALIZED = '/calendar-normalized',
  ROBOT_ID = '/robot/:id',
  ROBOT = '/robot/',
  MISSION_CONTROL = '/mission-control/:id',
  ANALYTICS = '/analytics',
  ANALYTICS_LEGACY = '/analytics-legacy',
  NOTIFICATION_SETTINGS = '/notification-settings',
  ACCOUNT = '/account',
  ACCOUNT_PROFILE = '/account/profile',
  ACCOUNT_USERS = '/account/users',
  ACCOUNT_ORGANIZATIONS = '/account/organizations',
  ACCOUNT_NOTIFICATIONS = '/account/notifications',
  ACCOUNT_NOTIFICATION_SETTINGS = '/account/notification-settings',
  ACCOUNT_FAVORITES = '/account/favorites',
  ERROR = '/error',
  NOT_FOUND = '/404',
  EMPTY = '',
  OTHERS = '*',
}

export enum PROGRESS_TYPES {
  IDLE = 'idle',
  WORK = 'work',
  SUCCESS = 'success',
  ERROR = 'error',
}

export enum LOGIN_ERROR_TYPES {
  NONE,
  INVALID_EMAIL_OR_PASSWORD,
  ERROR,
}

export enum LANGUAGES {
  RU = 'ru',
  EN = 'en',
}

export const enum MISSION_PAGE_MODES {
  VIEW_ZONES = 'view_zones',
  DRAW_ZONE = 'draw_zone',
  DRAW_ROUTE = 'draw_route',
  EDIT_ZONE = 'edit_zone',
  EDIT_ZONE_ROUTE = 'edit_zone_route',
  ADJUST_COVERAGE = 'adjust_coverage',
}

export const enum FILES_LOADING_TYPE {
  ROBOT_MAP = 'robot_map',
  ROUTE = 'route',
}

export const enum MISSION_MAP_DRAWING_MODES {
  VIEW_ZONES = 'view_zones',
  DRAW_POLYGON = 'draw_polygon',
  DRAW_ROUTE = 'draw_route',
  DIRECT_SELECT_ZONE = 'direct_select_zone',
  ADD_ZONE_LAUNCH_POINT = 'draw_point',
  DIRECT_SELECT_ZONE_LAUNCH_POINT = 'direct_select_point',
  DIRECT_SELECT_TRANSFER_ROUTE = 'direct_select_transfer_route',
  DIRECT_SELECT_ZONE_ROUTE = 'direct_select_zone_route',
  ADD_MISSION_START_POINT = 'draw_start_point',
  ADD_MISSION_FINISH_POINT = 'draw_finish_point',
  DIRECT_SELECT_MISSION_POINT = 'direct_select_mission_point',
}

export const enum MISSION_ZONE_TYPES {
  CLEANING = 'cleaning',
  RESTRICT = 'restrict',
  RECORDED = 'recorded',
}

/** Mission.robot_type codes for Autonomics robots. ComboOmni is shown as Omni in UI. */
export const MISSION_ROBOT_TYPES = ['Sweeper', 'Combo', 'ComboOmni'] as const

export const enum ROBOT_STATUSES {
  AVAILABLE = 'available',
  NOT_AVAILABLE = 'not_available',
}

export const enum MISSION_CALCULATING_STATUSES {
  NEW = 'new',
  CALCULATING = 'calculating',
  OK = 'ok',
  ERROR = 'error',
  PARTIAL_CALCULATED = 'partial_calculated',
  UNKNOWN = 'unknown',
}

export const enum TRANSFER_CALCULATING_STATUSES {
  NEW = 'new',
  CALCULATING = 'calculating',
  OK = 'ok',
  ERROR = 'error',
}

export const enum TASK_STATUSES {
  NEW = 'new',
  CALCULATING = 'scenario_calculating',
  OK = 'scenario_done',
  ERROR = 'scenario_error',
}

export const TIMEOUT_FETCHING_STATUS = 3000

export const TOKEN_MAPBOX =
  'pk.eyJ1IjoiaXJhZGlvbm92IiwiYSI6ImNtb2k3bmJlYTA2a2wycXFvd3E0dTlpeHkifQ.g0WsK4Jn3Tgi_bDkCU8hBw'

export const enum STYLES {
  VECTOR = 'mapbox://styles/mapbox/navigation-guidance-day-v4',
  SATELLITE = 'mapbox://styles/mapbox/satellite-v9',
  DARK = 'mapbox://styles/mapbox/navigation-guidance-night-v4',
}

export const enum GEOTIFF_LAYER_STATUSES {
  NEW = 'new',
  ERROR = 'error',
  PENDING = 'pending',
  OK = 'ok',
}

export const enum TRANSFER_TYPE {
  START,
  MIDDLE,
  FINISH,
}

export const enum ROBOT_MODES {
  MODE_NOT_SET = 0,
  MODE_MANUAL = 1,
  MODE_AUTOMATIC = 2,
  MODE_MAPPING = 6,
  MODE_ROUTE_RECORDING = 7,
  MODE_SWITCHING_FACILITY = 14,
  MODE_CALIBRATION_SERVICE_POINT = 15,
  MODE_RESET_CALIBRATION_SERVICE_POINT = 16,
  MODE_SERVICE_POINT = 17,
  MODE_SWITCHING_TO_LIDAR_LOCALIZATION = 100,
  MODE_SWITCHING_TO_GNSS_BASED_LOCALIZATION = 101,
  MODE_SWITCHING_TO_COMBINED_LOCALIZATION = 102,
  MODE_SWITCHING_NTRIP = 103,
  MODE_REBOOT_ROBOT = 253,
  MODE_UPDATING_FIRMWARE = 254,
}

export const enum ROBOT_COMMANDS {
  START = 1,
  STOP = 2,
  PAUSE = 3,
}

/**
 * Unified runtime state for “cleaning / mission task” UX (Autonomics: activeMode + activeCommand;
 * Gausium: mission_progress.state on the last odyssey snapshot).
 */
export const enum TASK_RUNTIME_STATE {
  RUNNING = 'running',
  PAUSED = 'paused',
  IDLE = 'idle',
  UNKNOWN = 'unknown',
}

export const enum ROBOT_NETWORK_STATUSES {
  ONLINE = 'online',
  OFFLINE = 'offline',
}

/**
 * Live Autonomics trail kept in Redux for the robot-on-map layer.
 * 2000 pts × 0.5 m spacing ≈ 1 km of path — enough to draw, not enough to
 * balloon tab memory (the previous 50_000 cap was ~25 km and still grew).
 */
export const MAX_TRAJECTORY_POINTS = 2000
/** Vertices fed to turf.buffer; full trail is downsampled to this first. */
export const MAX_TRAJECTORY_POLYGON_POINTS = 400
/** Mirrors backend `TRAIL_MAX_POINTS_PER_SESSION` (GS live execution). */
export const MAX_LIVE_EXECUTION_TRAIL_POINTS = 3000
/** Empirical GS command delay: values below are often rejected by vendor API. */
export const GAUSIUM_REMOTE_COMMAND_START_DELAY_MS = 60000

export const TRAJECTORY_LINE_WIDTH_IN_METERS = 0.5

export const DEFAULT_VIZUALIZATION_SETTINGS: IVisualizationSettings = {
  clean_fill_color: '#06b6d4',
  clean_border_color: '#0891b2',
  clean_width: 2,
  clean_opacity: 0.25,
  restrict_fill_color: '#f43f5e',
  restrict_border_color: '#e11d48',
  restrict_width: 2,
  restrict_opacity: 0.2,
  cleaning_route_color: '#3b82f6',
  cleaning_route_width: 1,
  cleaning_route_opacity: 1,
  transfer_route_color: '#3b82f6',
  transfer_route_width: 2,
  transfer_route_opacity: 0.8,
}

export const enum ROBOT_EQUIPMENT_COMMAND {
  // TRASH = 'trash_lock',
  BODY = 'body_lock',
  // FRONT = 'front_lock',
  // CHARGE = 'charge_lock',
  WATER = 'watering_command',
  LIGHT = 'light_command',
  DANGER = 'danger_button_command',
  CLEANING_EQUIPMENT = 'cleaning_equipment_command',
}

export const enum ROBOT_LOCALIZATION_COMMAND {
  ENABLE_LASERSCAN = 1,
  DISABLE_LASERSCAN = 2,
  SET_POSE = 3,
}

export const MAX_TRAJECTORY_DISTANCE = 0.5

// Feature toggle for adjustable equipment protocol
// This should match Django setting USE_ADJUSTABLE_EQUIPMENT_PROTOCOL
// Note: This is a frontend flag, backend has its own setting
// When false, equipment settings UI is hidden
export const USE_ADJUSTABLE_EQUIPMENT_PROTOCOL = true // TODO: Get from backend API or env variable

// Feature toggle to show/hide Settings tab in zone and recorded route creation/editing dialogs
export const SHOW_ZONE_SETTINGS_TAB = true

export const enum ROBOT_EQUIPMENT_STATES {
  MANUAL = 0,
  TRANSPORT = 1,
  WORKING = 2,
  EMERGENCY = 10,
  ERROR = 255,
}

// Equipment setup default values
export const DEFAULT_TURBINE_SPEED = 2700
export const DEFAULT_SCRUBBER_BRUSH_SPEED = 800
export const TURBINE_RPM_MIN = 0
export const TURBINE_RPM_MAX = 3000
export const SCRUBBER_BRUSH_RPM_MIN = 0
export const SCRUBBER_BRUSH_RPM_MAX = 1360
export const DEFAULT_FRONT_DUST_SUPPRESSION = true
export const DEFAULT_FRONT_LIGHTS = false
